import type React from 'react';
import { useIntl } from '@/react-intl';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline'

/* ──────────────────────────────────────────────
   Badge — small contextual pill used in the
   drawer ribbon (ID / Year / Workspace)
   ────────────────────────────────────────────── */
interface DrawerBadgeProps {
  children: React.ReactNode
  variant?: 'default' | 'accent' | 'muted' | 'danger' | 'warning' | 'success' | 'edit'
}

function DrawerBadge({ children, variant = 'default' }: DrawerBadgeProps) {
  // whitespace-nowrap : sur mobile le titre prend la place et un badge court (« 2026 ») se coupait en deux lignes.
  const base = 'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide uppercase'
  const variants = {
    default: 'bg-portal-surface-muted text-portal-ink/70',
    accent: 'bg-portal-accent/15 text-portal-accent',
    muted: 'bg-portal-surface-muted/60 text-portal-ink/50',
    danger: 'bg-portal-danger/15 text-danger-text',
    warning: 'bg-portal-warning/15 text-warning-text',
    success: 'bg-portal-success/15 text-success-text',
    edit: 'bg-portal-accent/20 text-portal-accent ring-1 ring-portal-accent/30',
  }
  return <span className={`${base} ${variants[variant]}`}>{children}</span>
}

/** Map a cdstatus value to a DrawerBadge variant */
function statusToBadgeVariant(status: string): DrawerBadgeProps['variant'] {
  switch (status) {
    case 'build': return 'danger'
    case 'operate': return 'warning'
    case 'verify': return 'warning'
    case 'divest': return 'warning'
    case 'close': return 'success'
    case 'delete': return 'accent'
    default: return 'default'
  }
}

/* ──────────────────────────────────────────────
   RightDrawer — slide-in panel from the right.

   Accessibility (WCAG 4.1.2 / 2.4.3):
   Headless UI Dialog provides role="dialog", aria-modal="true",
   aria-labelledby (wired to DialogTitle), focus trap,
   Escape close, and focus return to the triggering element.
   ────────────────────────────────────────────── */

type RightDrawerProps = {
  isOpen: boolean
  title: React.ReactNode
  /** Optional badge chips shown beside the title (e.g. ID, Year, Workspace) */
  badges?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  headerActions?: React.ReactNode
  showBackdrop?: boolean
  backdropClassName?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  widthClassName?: string
}

const sizeToWidthClassName: Record<NonNullable<RightDrawerProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-full'
}

const RightDrawer = ({
  isOpen,
  title,
  badges,
  onClose,
  children,
  footer,
  headerActions,
  showBackdrop = true,
  backdropClassName,
  size = 'lg',
  widthClassName
}: RightDrawerProps) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const resolvedWidthClassName = widthClassName ?? sizeToWidthClassName[size]
  const resolvedBackdropClass = backdropClassName ?? 'bg-black/20 backdrop-blur-[2px]'

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop — animated via DialogBackdrop transition */}
      {showBackdrop && (
        <DialogBackdrop
          transition
          className={`fixed inset-0 z-40 transition-opacity duration-300 data-[closed]:opacity-0 ${resolvedBackdropClass}`}
        />
      )}

      {/* Drawer panel */}
      <div className="fixed inset-0 z-50 flex justify-end overflow-hidden">
        <DialogPanel
          transition
          className={`geode-mobile-drawer flex h-screen w-full ${resolvedWidthClassName} flex-col bg-portal-surface shadow-2xl ring-1 ring-portal-border/10 transition-all duration-300 ease-out data-[closed]:translate-x-full rtl:data-[closed]:-translate-x-full data-[closed]:opacity-0`}
        >
          {/* ─── Ribbon header ─── */}
          <div className="relative border-b border-portal-border/40 bg-gradient-to-r from-portal-surface-muted/80 to-portal-surface px-6 py-5">
            {/* Accent stripe */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r rtl:bg-gradient-to-l from-portal-accent/80 via-portal-accent/40 to-transparent" />

            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* Title row */}
                <div className="flex items-center gap-3">
                  <DialogTitle className="text-xl font-bold text-portal-ink tracking-tight truncate">
                    {title}
                  </DialogTitle>
                  {badges && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {badges}
                    </div>
                  )}
                </div>
                {/* Header actions row */}
                {headerActions != undefined && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {headerActions}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-1 min-h-11 min-w-11 rounded-lg p-2 text-portal-ink/50 transition-all hover:bg-portal-surface-muted hover:text-portal-ink focus:outline-none focus:ring-2 focus:ring-portal-accent/40"
                aria-label={t('core.action.close')}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* ─── Content ─── */}
          <div className="flex-1 overflow-y-auto bg-portal-surface px-6 py-6">
            {children}
          </div>

          {/* ─── Footer ─── */}
          {footer != undefined && (
            <div className="border-t border-portal-border/40 bg-portal-surface-muted/30 px-6 py-4">
              {footer}
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default RightDrawer
export { DrawerBadge, statusToBadgeVariant }
